/**
 * TanStack Query hook for bulk-importing subjects via the UAS bulk import endpoint
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { isApiError } from '@/lib/api-client'
import { bulkImportAttributes } from '@/lib/uas-api'
import { subjectKeys } from '@/hooks/use-subjects'

export interface BulkImportItem {
  readonly subjectIdentifier: string
  readonly identifierType: 'EMAIL' | 'EMPLOYEE_ID' | 'USERNAME' | 'EXTERNAL_ID'
  readonly attributes: ReadonlyArray<{
    readonly attributeKey: string
    readonly value: string
    readonly dataType: 'STRING'
  }>
}

export interface BulkImportRequest {
  readonly items: ReadonlyArray<BulkImportItem>
  readonly source: string
}

export interface BulkImportResult {
  readonly totalCreated: number
  readonly totalUpdated: number
  readonly errorCount: number
}

export function useSubjectUpload() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BulkImportRequest) => bulkImportAttributes(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: subjectKeys.lists() })
      if (result.totalCreated > 0) {
        toast.success(
          `Imported ${result.totalCreated} new subject(s), ${result.totalUpdated} attribute(s) set`
        )
      } else if (result.totalUpdated > 0) {
        toast.success(`Updated ${result.totalUpdated} attribute(s)`)
      }
    },
    onError: (error) => {
      const message = isApiError(error) ? error.message : 'An unexpected error occurred'
      toast.error(`Bulk import failed: ${message}`)
    },
  })
}
